import React, { useState } from 'react';
import { BookOpen, MessageSquare, BarChart3, CheckCircle2, Flame } from 'lucide-react';

// Sample topic-mastery bars for the Analytics Dashboard preview - illustrative
// UI content (like the sample quiz question above it), not a real user's data.
const TOPIC_MASTERY = [
  { topic: 'Physics - Mechanics', percent: 82 },
  { topic: 'Chemistry - Organic', percent: 58 },
  { topic: 'Mathematics - Calculus', percent: 71 },
];

// Simplified version of www.rudvan.online's interactive "See It In Action"
// demo (2026-08-07). That page runs a fully stateful mock quiz/chat/
// analytics simulator with sample data; rebuilding that live simulation
// was out of scope here, so this keeps the three-view structure but as a
// single static preview per tab rather than a scripted interaction.
const VIEWS = [
  {
    key: 'quiz',
    label: 'Quiz Interface',
    icon: BookOpen,
  },
  {
    key: 'tutor',
    label: 'AI Tutor (Lexa)',
    icon: MessageSquare,
  },
  {
    key: 'analytics',
    label: 'Analytics Dashboard',
    icon: BarChart3,
  },
];

export default function DemoSection({ isDarkTheme }) {
  const [active, setActive] = useState('quiz');
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';
  const panelBg = isDarkTheme ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200';
  const fieldBg = isDarkTheme ? 'bg-white/5 border-white/10' : 'bg-surface-light border-gray-200';

  return (
    <section id="demo" className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-10">
        <h2 className={`font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${textColor}`}>
          See It In Action
        </h2>
        <p className={`text-lg md:text-xl ${textMuted}`}>A preview of what the adaptive learning experience looks like</p>
      </div>

      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {VIEWS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-btn font-semibold text-sm transition-all ${
              active === key
                ? 'bg-brand-blue text-white shadow-brand-btn'
                : `border ${fieldBg} ${textMuted} hover:${textColor}`
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      <div className={`rounded-card border ${panelBg} shadow-brand-lg p-6 sm:p-8`}>
        {active === 'quiz' && (
          <div className="grid lg:grid-cols-[1fr_280px] gap-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm ${textMuted}`}>Question 12 of 15</span>
              </div>
              <div className={`h-2 rounded-full ${fieldBg} border-0 mb-6 overflow-hidden`}>
                <div className="h-full w-4/5 bg-brand-gradient rounded-full" />
              </div>
              <p className={`font-semibold ${textColor} mb-4`}>
                A compound X has molecular formula C₂H₆O. It doesn't give the Tollens test. What is its structure?
              </p>
              <div className={`rounded-btn border ${fieldBg} px-4 py-3 flex items-center gap-3`}>
                <div className={`w-4 h-4 rounded-full border-2 ${isDarkTheme ? 'border-white/40' : 'border-gray-300'}`} />
                <span className={textMuted}>CH₃CH₂OH (Ethanol)</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-btn border-2 border-brand-blue bg-brand-blue/10 px-4 py-3">
                <p className={`text-xs font-bold ${isDarkTheme ? 'text-brand-cyan' : 'text-brand-blue'} mb-1`}>
                  AI Difficulty Adjustment
                </p>
                <p className={`text-sm ${textMuted}`}>
                  You're solving these steadily - difficulty has been increased to <strong className={textColor}>Advanced</strong>.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-btn border ${fieldBg} px-4 py-3 text-center`}>
                  <p className={`text-xs ${textMuted} mb-1`}>Accuracy</p>
                  <p className={`text-xl font-bold ${textColor}`}>88%</p>
                </div>
                <div className={`rounded-btn border ${fieldBg} px-4 py-3 text-center`}>
                  <p className={`text-xs ${textMuted} mb-1`}>Avg Time</p>
                  <p className={`text-xl font-bold ${textColor}`}>1m 24s</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {active === 'tutor' && (
          <div className="space-y-4">
            <div className={`rounded-btn border ${fieldBg} px-4 py-3 max-w-md`}>
              <p className={`text-sm ${textMuted}`}>Why doesn't ethanol give a positive Tollens test?</p>
            </div>
            <div className="rounded-btn bg-brand-gradient px-4 py-3 max-w-md ml-auto">
              <p className="text-sm text-white">
                The Tollens test needs an aldehyde group. Ethanol is an alcohol, not an aldehyde - Lexa can walk
                through the functional-group check step by step whenever you're stuck.
              </p>
            </div>
          </div>
        )}

        {active === 'analytics' && (
          <div className="grid lg:grid-cols-[1fr_280px] gap-6">
            <div>
              <p className={`text-sm font-semibold ${textColor} mb-4`}>Mastery by topic</p>
              <div className="space-y-4">
                {TOPIC_MASTERY.map(({ topic, percent }) => (
                  <div key={topic}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-sm ${textMuted}`}>{topic}</span>
                      <span className={`text-sm font-bold ${textColor}`}>{percent}%</span>
                    </div>
                    <div className={`h-2 rounded-full ${fieldBg} border-0 overflow-hidden`}>
                      <div className="h-full bg-brand-gradient rounded-full" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className={`rounded-btn border ${fieldBg} px-4 py-3 flex items-center gap-3`}>
                <Flame className="text-brand-blue shrink-0" size={18} />
                <span className={`text-sm ${textColor}`}>Weak areas surfaced automatically</span>
              </div>
              <div className={`rounded-btn border ${fieldBg} px-4 py-3 flex items-center gap-3`}>
                <CheckCircle2 className="text-brand-blue shrink-0" size={18} />
                <span className={`text-sm ${textColor}`}>Study time logged per session</span>
              </div>
              <div className={`rounded-btn border ${fieldBg} px-4 py-3 flex items-center gap-3`}>
                <BarChart3 className="text-brand-blue shrink-0" size={18} />
                <span className={`text-sm ${textColor}`}>Progress trends over the term</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
